import { create } from 'zustand';
import type { EmailPreviewDto, EmailTemplateDto, UpdateEmailTemplateRequest } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * The wording of transactional email (API → Store → Render, ADR-008).
 *
 * The catalog is fixed by the API — a store edits what the platform sends, it
 * does not invent new moments to send at. So this store never creates or
 * deletes; it saves wording and resets it.
 */

interface EmailTemplatesState {
  templates: EmailTemplateDto[];
  loading: boolean;
  saving: boolean;
  /** Which template was just saved, for the inline confirmation. */
  savedKey: string | null;
  error: string | null;

  preview: EmailPreviewDto | null;
  previewLoading: boolean;
  /** Result of the last test send: the address it went to, or a failure. */
  testResult: { to: string; driver: string } | null;

  load: () => Promise<void>;
  save: (key: string, body: UpdateEmailTemplateRequest) => Promise<boolean>;
  reset: (key: string) => Promise<void>;
  loadPreview: (key: string) => Promise<void>;
  clearPreview: () => void;
  sendTest: (key: string, to: string) => Promise<boolean>;
}

export const useEmailTemplatesStore = create<EmailTemplatesState>((set) => ({
  templates: [],
  loading: false,
  saving: false,
  savedKey: null,
  error: null,
  preview: null,
  previewLoading: false,
  testResult: null,

  async load() {
    set({ loading: true, error: null });

    try {
      const templates = await withSession(() => adminApi.emailTemplates.list());
      set({ templates, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the email templates.') });
    }
  },

  async save(key, body) {
    set({ saving: true, error: null, savedKey: null });

    try {
      const updated = await withSession(() => adminApi.emailTemplates.update(key, body));
      set(replace(updated, { saving: false, savedKey: key }));
      return true;
    } catch (error) {
      // The API names the offending placeholders; that message is the useful one.
      set({ saving: false, error: errorMessage(error, 'Could not save this template.') });
      return false;
    }
  },

  async reset(key) {
    set({ saving: true, error: null, savedKey: null });

    try {
      const restored = await withSession(() => adminApi.emailTemplates.reset(key));
      set(replace(restored, { saving: false, savedKey: key }));
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not restore the default wording.') });
    }
  },

  async loadPreview(key) {
    set({ previewLoading: true, preview: null });

    try {
      const preview = await withSession(() => adminApi.emailTemplates.preview(key));
      set({ preview, previewLoading: false });
    } catch (error) {
      set({ previewLoading: false, error: errorMessage(error, 'Could not render the preview.') });
    }
  },

  clearPreview() {
    set({ preview: null, testResult: null });
  },

  async sendTest(key, to) {
    set({ error: null, testResult: null });

    try {
      const result = await withSession(() => adminApi.emailTemplates.sendTest(key, to));
      set({ testResult: result });
      return true;
    } catch (error) {
      set({ error: errorMessage(error, 'Could not send the test email.') });
      return false;
    }
  },
}));

/** Swaps one template in place, leaving the rest of the list untouched. */
function replace(updated: EmailTemplateDto, rest: Partial<EmailTemplatesState>) {
  return (state: EmailTemplatesState): Partial<EmailTemplatesState> => ({
    ...rest,
    templates: state.templates.map((template) =>
      template.key === updated.key ? updated : template,
    ),
  });
}

/** The current draft is compared against this to enable the save button. */
export function isUnchanged(template: EmailTemplateDto, draft: UpdateEmailTemplateRequest): boolean {
  return (
    template.subjectAr === draft.subjectAr &&
    template.subjectEn === draft.subjectEn &&
    template.bodyAr === draft.bodyAr &&
    template.bodyEn === draft.bodyEn &&
    template.isEnabled === (draft.isEnabled ?? true)
  );
}
